import type { SerializedHeadingNode } from '@payloadcms/richtext-lexical'
import type { SerializedLexicalNode } from 'lexical'

import { slugifyHeadingId, uniqueHeadingIds } from '@/utilities/slugifyHeadingId'

/** Re-export: IDs de heading (minúsculas, hífen, sem acentos, únicos). */
export { slugifyHeadingId, uniqueHeadingIds }

export type TocHeadingLevel = 'h2' | 'h3' | 'h4'

export type TocEntry = {
  id: string
  /** Texto original do heading (slug / JSON-LD). */
  text: string
  /**
   * Rótulo só para o TOC quando há títulos repetidos (evita linhas visualmente idênticas).
   * Ex.: segundo «Introdução» → «Introdução (2)».
   */
  tocDisplayLabel?: string
  tag: TocHeadingLevel
}

/** Nó da árvore (H2 → H3 → H4) para renderização do TOC. */
export type TocTreeNode = {
  id: string
  text: string
  tag: TocHeadingLevel
  children: TocTreeNode[]
}

/**
 * Agrupa entradas planas em árvore: H2 raiz; H3 sob o H2 corrente;
 * H4 sob o H3 corrente (ou sob o H2 se não houver H3 antes).
 */
export function buildTocTree(entries: readonly TocEntry[]): TocTreeNode[] {
  const tree: TocTreeNode[] = []
  let currentH2: TocTreeNode | null = null
  let currentH3: TocTreeNode | null = null

  const label = (e: { id: string; text: string; tocDisplayLabel?: string; tag: TocHeadingLevel }) =>
    e.tocDisplayLabel ?? e.text

  for (const e of entries) {
    if (e.tag === 'h2') {
      currentH2 = { id: e.id, text: label(e), tag: e.tag, children: [] }
      tree.push(currentH2)
      currentH3 = null
    } else if (e.tag === 'h3') {
      const node: TocTreeNode = { id: e.id, text: label(e), tag: e.tag, children: [] }
      if (currentH2) {
        currentH2.children.push(node)
      } else {
        tree.push(node)
      }
      currentH3 = node
    } else if (e.tag === 'h4') {
      const node: TocTreeNode = { id: e.id, text: label(e), tag: e.tag, children: [] }
      if (currentH3) {
        currentH3.children.push(node)
      } else if (currentH2) {
        currentH2.children.push(node)
      } else {
        tree.push(node)
      }
    }
  }

  return tree
}

/** Texto simples a partir de nós Lexical (heading, formatação inline, etc.). */
export function extractPlainTextFromLexicalNodes(nodes: SerializedLexicalNode[] | undefined): string {
  if (!nodes?.length) return ''
  let s = ''
  for (const n of nodes) {
    if (n.type === 'text' && 'text' in n && typeof (n as { text?: string }).text === 'string') {
      s += (n as { text: string }).text
    }
    const children = (n as { children?: SerializedLexicalNode[] }).children
    if (Array.isArray(children)) {
      s += extractPlainTextFromLexicalNodes(children)
    }
  }
  return s.trim()
}

type LexicalRootish = { root?: { children?: SerializedLexicalNode[] } } | null | undefined

/**
 * Texto do primeiro parágrafo com conteúdo no corpo (paridade Yoast: o «resumo» é o primeiro `<p>`,
 * não a meta descrição).
 */
export function extractFirstBodyParagraphPlain(editorState: LexicalRootish): string {
  const children = editorState?.root?.children
  if (!Array.isArray(children)) return ''
  for (const n of children) {
    if (n.type !== 'paragraph') continue
    const pChildren = (n as { children?: SerializedLexicalNode[] }).children
    const text = extractPlainTextFromLexicalNodes(pChildren).replace(/\s+/g, ' ').trim()
    if (text) return text
  }
  return ''
}

/**
 * Percorre o documento Lexical em profundidade (ordem igual à renderização em fluxo).
 * Por omissão inclui **h2**, **h3** e **h4**; passe `levels` para restringir.
 */
export function extractTocEntriesFromLexical(
  editorState: LexicalRootish,
  options?: { levels?: readonly TocHeadingLevel[] },
): TocEntry[] {
  const levels = options?.levels ?? (['h2', 'h3', 'h4'] as const)
  const levelSet = new Set<string>(levels)
  const found: { text: string; tag: TocHeadingLevel }[] = []

  function walk(nodes: SerializedLexicalNode[] | undefined): void {
    if (!nodes) return
    for (const node of nodes) {
      if (node.type === 'heading') {
        const h = node as SerializedHeadingNode
        const tag = h.tag as TocHeadingLevel
        if (levelSet.has(tag)) {
          const text = extractPlainTextFromLexicalNodes(h.children as SerializedLexicalNode[])
          if (text) found.push({ text, tag })
        }
      }
      const children = (node as { children?: SerializedLexicalNode[] }).children
      if (Array.isArray(children)) walk(children)
    }
  }

  walk(editorState?.root?.children)

  const ids = uniqueHeadingIds(found.map((f) => f.text))
  const seenTitle = new Map<string, number>()

  return found.map((f, i) => {
    const key = f.text.trim().toLowerCase()
    const n = (seenTitle.get(key) ?? 0) + 1
    seenTitle.set(key, n)
    const tocDisplayLabel = n === 1 ? undefined : `${f.text} (${n})`

    return {
      id: ids[i]!,
      text: f.text,
      ...(tocDisplayLabel ? { tocDisplayLabel } : {}),
      tag: f.tag,
    }
  })
}
